import { useState } from "react";
import { DropdownButton } from "../atoms/DropdownButton";
import { LinkButton } from "../atoms/LinkButton";
import { PageHeader } from "../molecules/PageHeader";
import { SectionHeader } from "../molecules/SectionHeader";
import { StatsGrid } from "../organisms/StatsGrid";
import { User, Profile2User, Moneys, MessageText } from "iconsax-react";

import {
  PLATFORM_STATS,
  REVENUE_STATS,
  ACTIVITIES,
  DASHBOARD_GROUPS,
  TOP_PERFORMING_GROUPS,
  TOP_EARNING_GROUPS,
  CHART_DATA,
  type ActivityItem,
  type TopGroup,
} from "@/data";


const ActivityCard = ({ item }: { item: ActivityItem }) => {
  const getIcon = () => {
    switch (item.type) {
      case "group":
        return (
          <Profile2User variant="Bold" color="#FEFEFE" className="w-5 h-5" />
        );
      case "user":
        return <User variant="Bold" color="#FEFEFE" className="w-5 h-5" />;
      case "chat":
        return (
          <MessageText variant="Bold" color="#FEFEFE" className="w-5 h-5" />
        );
      case "payment":
        return <Moneys variant="Bold" color="#FEFEFE" className="w-5 h-5" />;
    }
  };

  const getLabel = () => {
    switch (item.type) {
      case "group":
        return "New group";
      case "user":
        return "New user";
      case "chat":
        return "Chat started";
      case "payment":
        return "Payment";
    }
  };

  return (
    <div className="flex flex-col items-start pb-4 border-b border-dark-30 last:border-b-0">
      <p className="text-sm text-dark-text mb-2 py-0.5 px-4 rounded-sm bg-dark-10 border border-dark-20">{getLabel()}</p>
      <div className="flex items-start gap-2">
        <div className="p-1 bg-dark-40 rounded-full flex justify-center mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-dark-text">{item.title}</h4>
          <p className="text-xsm text-dark-100">
            {item.subtitle}{" "}
            {item.detail && (
              <span className="text-dark-text ml-1 bg-dark-20 px-2 text-xsm rounded-[3px]">{item.detail}</span>
            )}
          </p>
          <p className="text-xsm text-dark-80 mt-0.5">{item.timestamp}</p>
        </div>
      </div>
    </div>
  );
};

const TopGroupItem = ({
  group,
  index,
  showRevenue = false,
}: {
  group: TopGroup;
  index: number;
  showRevenue?: boolean;
}) => (
  <div className="flex items-start gap-2 pb-3 border-b border-dark-30 last:border-b-0">
    <span className="text-sm text-dark-100 mt-0.5">{index}.</span>
    <Profile2User variant="Bold" color="#FEFEFE" className="w-5 h-5 mt-0.5" />
    <div className="flex-1 min-w-0">
      <h4 className="text-sm font-medium text-dark-text">{group.name}</h4>
      <p className="text-xsm text-dark-100">
        {group.users && `${group.users} users - `}Created By {group.creator}
        {group.isFree !== undefined && (
          <span className="ml-2 text-dark-text">
            {group.isFree ? "Free" : `$${group.revenue}`}
          </span>
        )}
        {showRevenue && (
          <span className="ml-2 text-dark-text">
            ${group.revenue.toLocaleString()}
          </span>
        )}
      </p> 
    </div>
  </div>
);

const Dashboard = () => {
  const [activityTab] = useState<"users" | "groups" | "messages" | "dates">(
    "users"
  );

  const maxValue = Math.max(...CHART_DATA.map((d) => d.value));

  return (
    <div className="max-w-7xl mx-auto space-y-10">
      {/* Platform Overview */}
      <PageHeader title="Platform Overview" />

      <section>
        <StatsGrid stats={PLATFORM_STATS} columns={3} />
      </section>

      {/* User Activity & Top Groups */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Activity Trends */}
        <div className="bg-dark-10 rounded-[5px] p-6">
          <div className="flex items-center justify-start gap-4.5 mb-4">
            <h2 className="text-xl font-medium text-dark-text">
              User Activity Trends
            </h2>
            <DropdownButton
              label="7 days"
              options={[
                { label: "7 days", value: "7d" },
                { label: "30 days", value: "30d" },
                { label: "90 days", value: "90d" },
              ]}
              onSelect={(value) => console.log("Selected:", value)}
            />
          </div>

          <div className="flex gap-6 mb-6 border-b border-dark-30">
            {["Users", "Groups", "Messages", "Dates Scheduled"].map((tab) => (
              <button
                key={tab}
                className={`pb-2 text-sm ${
                  tab.toLowerCase().replace(" ", "") === activityTab ||
                  (tab === "Users" && activityTab === "users")
                    ? "text-dark-text border-b-2 border-primary font-medium"
                    : "text-dark-80"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Chart */}
          <div className="h-[200px] flex items-end justify-between gap-2">
            {CHART_DATA.map((data, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-white rounded-t"
                  style={{ height: `${(data.value / maxValue) * 100}%` }}
                />
                <span className="text-xsm text-dark-80">{data.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performing Groups */}
        <div className="bg-dark-10 rounded-[5px] p-6">
          <SectionHeader
            title="Top Performing Groups"
            action={
              <DropdownButton
                label="7 days"
                options={[
                  { label: "7 days", value: "7d" },
                  { label: "30 days", value: "30d" },
                  { label: "90 days", value: "90d" },
                ]}
                onSelect={(value) => console.log("Selected:", value)}
              />
            }
          />

          <div className="space-y-3 mb-6">
            {TOP_PERFORMING_GROUPS.map((group, i) => (
              <TopGroupItem key={i} group={group} index={i + 1} />
            ))}
          </div>

          <LinkButton
            label="View Group Stats"
            onClick={() => console.log("Navigate to group stats")}
            // or with href
            // href="/groups/stats"
          />
        </div>
      </section>

      {/* Real-Time Activity & Group Table */}
      <section className="grid grid-cols-1 gap-6">
        {/* Real-Time App Activity */}
        <div>
          <h2 className="text-xl font-medium text-dark-text mb-6">
            Real-Time App Activity
          </h2>
          <div className="space-y-4">
            {ACTIVITIES.map((activity, i) => (
              <ActivityCard key={i} item={activity} />
            ))}
          </div>
        </div>

        {/* Group Table */}
        <div>
          <SectionHeader
            title="Group Table"
            action={
              <LinkButton
                label="View Group Stats"
                onClick={() => console.log("Navigate to group stats")}
              />
            }
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-30">
                  <th className="text-left py-3 text-dark-100 font-medium">
                    Group Name
                  </th>
                  <th className="text-left py-3 text-dark-100 font-medium">
                    Creator
                  </th>
                  <th className="text-left py-3 text-dark-100 font-medium">
                    Type
                  </th>
                  <th className="text-left py-3 text-dark-100 font-medium">
                    Users Joined
                  </th>
                  <th className="text-left py-3 text-dark-100 font-medium">
                    Revenue
                  </th>
                  <th className="text-left py-3 text-dark-100 font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {DASHBOARD_GROUPS.map((group, i) => (
                  <tr key={i} className="border-b border-dark-30 last:border-0">
                    <td className="py-3 text-dark-text">{group.name}</td>
                    <td className="py-3 text-dark-text">{group.creator}</td>
                    <td className="py-3 text-dark-text">
                      {group.type}
                      {group.price && ` ($${group.price})`}
                    </td>
                    <td className="py-3 text-dark-text">{group.usersJoined}</td>
                    <td className="py-3 text-dark-text">${group.revenue}</td>
                    <td className="py-3 text-dark-text">{group.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Revenue Summary */}
      <section>
        <SectionHeader
          title="Revenue Summary"
          action={
            <DropdownButton
              label="Last 30 days"
              options={[
                { label: "Last 7 days", value: "7d" },
                { label: "Last 30 days", value: "30d" },
                { label: "Last 90 days", value: "90d" },
              ]}
              onSelect={(value) => console.log("Selected:", value)}
            />
          }
        />

        <div className="mb-8">
          <StatsGrid stats={REVENUE_STATS} columns={3} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart Placeholder */}
          <div className="bg-dark-10 rounded-[5px] p-6">
            <DropdownButton
              label="Last 30 days"
              options={[
                { label: "Last 7 days", value: "7d" },
                { label: "Last 30 days", value: "30d" },
                { label: "Last 90 days", value: "90d" },
              ]}
              onSelect={(value) => console.log("Selected:", value)}
            />

            {/* Donut Chart Placeholder */}
            <div className="flex items-center justify-center h-[250px] mt-6">
              <div className="relative w-[200px] h-[200px]">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#3DD68C"
                    strokeWidth="20"
                    strokeDasharray="157 314"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#4A9EFF"
                    strokeWidth="20"
                    strokeDasharray="157 314"
                    strokeDashoffset="-157"
                  />
                </svg>
              </div>
            </div>

            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#4A9EFF] rounded-sm" />
                <span className="text-sm text-dark-100">
                  Platform Commission
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#3DD68C] rounded-sm" />
                <span className="text-sm text-dark-100">Creator Earnings</span>
              </div>
            </div>
          </div>

          {/* Top Earning Groups */}
          <div className="bg-dark-10 rounded-[5px] p-6">
            <h3 className="text-xl font-medium text-dark-text mb-6">
              Top Earning Groups
            </h3>
            <div className="space-y-3 mb-6">
              {TOP_EARNING_GROUPS.map((group, i) => (
                <TopGroupItem key={i} group={group} index={i + 1} showRevenue />
              ))}
            </div>
            <LinkButton
              label="View Group Stats"
              onClick={() => console.log("Navigate to group stats")}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
